import Vue from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import axios from '@app2/api/axios'

const namespaced = true

const TERMINAL_STATUSES = ['completed', 'failed']
export const isBuildTerminal = (build) =>
  !!(build && build.platform_builds && build.platform_builds.length > 0 &&
  build.platform_builds.every(pb => TERMINAL_STATUSES.includes(pb.status)))

export function formatPlatformStatus(raw) {
  switch (raw) {
    case 'completed': return 'Live'
    case 'failed':    return 'Failed'
    case 'building':  return 'Building'
    case 'queued':    return 'Queued'
    default:          return 'Unbuilt'
  }
}

const state = () => ({
  config: null,
  originalConfig: null,
  stagedCredentials: {},
  presence: {},
  loading: false,
  saving: false,
  openPanel: null,
  openModal: null,
  fieldErrors: {},
  activeBuild: null,
  buildHistory: [],
  buildHistoryMeta: { current_page: 1, last_page: 1, total: 0, per_page: 20 },
  buildHistoryLoading: false,
  pollingTimer: null,
  publishRequest: null,
})

const mutations = {
  SET_LOADING(state, v) { state.loading = v },
  SET_SAVING(state, v) { state.saving = v },
  SET_CONFIG(state, config) {
    state.config = cloneDeep(config)
    state.originalConfig = cloneDeep(config)
  },
  UPDATE_CONFIG_FIELD(state, { field, value }) {
    Vue.set(state.config, field, value)
    // M2: when the user types a non-blank app_name, auto-clear the
    // "App name is required." fieldError so the inline validation
    // message goes away as soon as they fix it. Other field errors
    // are not auto-cleared — they need an explicit save attempt.
    if (
      field === 'app_name'
      && typeof value === 'string'
      && value.trim()
      && state.fieldErrors
      && state.fieldErrors.app_name
    ) {
      const next = { ...state.fieldErrors }
      delete next.app_name
      state.fieldErrors = next
    }
  },
  SET_PRESENCE(state, presence) {
    state.presence = { ...presence }
  },
  STAGE_CREDENTIALS(state, partial) {
    state.stagedCredentials = { ...state.stagedCredentials, ...partial }
  },
  CLEAR_STAGED_CREDENTIALS(state) {
    state.stagedCredentials = {}
  },
  SET_OPEN_PANEL(state, panel) { state.openPanel = panel },
  SET_OPEN_MODAL(state, modal) { state.openModal = modal },
  SET_FIELD_ERRORS(state, errors) { state.fieldErrors = errors || {} },
  CLEAR_FIELD_ERRORS(state) { state.fieldErrors = {} },
  RESET_CONFIG_TO_ORIGINAL(state) {
    state.config = cloneDeep(state.originalConfig)
    state.stagedCredentials = {}
    state.fieldErrors = {}
  },
  SET_ACTIVE_BUILD(state, build) { state.activeBuild = build },
  SET_BUILD_HISTORY(state, builds) { state.buildHistory = builds },
  SET_BUILD_HISTORY_META(state, meta) { state.buildHistoryMeta = meta },
  SET_BUILD_HISTORY_LOADING(state, v) { state.buildHistoryLoading = v },
  SET_POLLING_TIMER(state, timer) { state.pollingTimer = timer },
  SET_PUBLISH_REQUEST(state, req) { state.publishRequest = req },
  RESET_ALL(state) {
    if (state.pollingTimer) {
      clearInterval(state.pollingTimer)
      state.pollingTimer = null
    }
    state.config = null
    state.originalConfig = null
    state.stagedCredentials = {}
    state.presence = {}
    state.loading = false
    state.saving = false
    state.openPanel = null
    state.openModal = null
    state.fieldErrors = {}
    state.activeBuild = null
    state.buildHistory = []
    state.buildHistoryMeta = { current_page: 1, last_page: 1, total: 0, per_page: 20 }
    state.buildHistoryLoading = false
    state.publishRequest = null
  },
}

const CRED_FIELD_TO_MODAL = {
  // Apple build credentials modal
  ios_asc_key: 'apple-build-credentials',
  ios_asc_key_id: 'apple-build-credentials',
  ios_asc_issuer_id: 'apple-build-credentials',
  ios_team_id: 'apple-build-credentials',
  // Apple push credentials modal
  apple_push_cert: 'apple-push-credentials',
  apns_key_id: 'apple-push-credentials',
  // Google build credentials modal
  android_google_play_key: 'google-build-credentials',
  android_sha256_fingerprint: 'google-build-credentials',
  // Google push credentials modal
  firebase_service_account: 'google-push-credentials',
  // Identifier fields
  ios_bundle_id: 'bundle-id',
  android_package_name: 'package-name',
}

const actions = {
  async save({ commit, state }, { hub, silent = false } = {}) {
    commit('SET_SAVING', true)
    if (!silent) commit('CLEAR_FIELD_ERRORS')
    try {
      const payload = { ...state.config, ...state.stagedCredentials }
      const { data } = await axios.put(`/api/mobile-apps/hubs/${hub.id}`, payload)
      const resource = data.data
      const {
        has_ios_store_credentials, has_android_store_credentials,
        has_apple_push_cert, has_apns_key_id, has_firebase_service_account,
        ...config
      } = resource
      commit('SET_CONFIG', config)
      commit('SET_PRESENCE', {
        has_ios_store_credentials, has_android_store_credentials,
        has_apple_push_cert, has_apns_key_id, has_firebase_service_account,
      })
      commit('CLEAR_STAGED_CREDENTIALS')
    } catch (e) {
      // NOTE: the platform 2.0 axios interceptor (resources/2.0/js/api/axios.js:66)
      // throws `error.response` verbatim — not the original axios Error — so `e`
      // here is `{ data, status, ... }`, not a wrapped error object. Read status
      // off `e` directly; `e.response` is always undefined.
      const is422 = e && e.status === 422
      // Silent mode (auto-save) swallows 422 because partial credentials mid-typing
      // would otherwise re-open a modal and surface transient validation toasts.
      // Network/server errors still bubble up so the caller can toast them.
      if (silent && is422) return
      if (!silent && is422) {
        const errors = (e.data && e.data.errors) || {}
        const mapped = Object.fromEntries(
          Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
        )
        commit('SET_FIELD_ERRORS', mapped)
        const failingModals = new Set(
          Object.keys(mapped)
            .map(k => CRED_FIELD_TO_MODAL[k])
            .filter(Boolean)
        )
        // Surface the first modal whose fields failed validation.
        const modalPriority = [
          'apple-build-credentials',
          'apple-push-credentials',
          'google-build-credentials',
          'google-push-credentials',
          'bundle-id',
          'package-name',
        ]
        const firstFailing = modalPriority.find(m => failingModals.has(m))
        if (firstFailing) {
          commit('SET_OPEN_MODAL', firstFailing)
        }
      }
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async fetchConfig({ commit }, { hub }) {
    commit('SET_LOADING', true)
    try {
      const { data } = await axios.get(`/api/mobile-apps/hubs/${hub.id}`)
      const resource = data.data
      const {
        has_ios_store_credentials = false,
        has_android_store_credentials = false,
        has_apple_push_cert = false,
        has_apns_key_id = false,
        has_firebase_service_account = false,
        ...config
      } = resource
      commit('SET_CONFIG', config)
      commit('SET_PRESENCE', {
        has_ios_store_credentials,
        has_android_store_credentials,
        has_apple_push_cert,
        has_apns_key_id,
        has_firebase_service_account,
      })
    } catch (e) {
      // See the save() action note: axios interceptor throws `error.response`,
      // so the status lives on `e` directly.
      if (e && e.status === 404) {
        commit('SET_CONFIG', {
          app_name: hub.title || '',
          // primary_domain removed — backend reads from hub instead.
          // universal_links_enabled and app_links_enabled NOT seeded — DB defaults
          // (both true) take effect on first row creation. Frontend shows the
          // toggles only after creds exist; by then the resource has refetched
          // with server-side defaults applied.
        })
        commit('SET_PRESENCE', {
          has_ios_store_credentials: false,
          has_android_store_credentials: false,
          has_apple_push_cert: false,
          has_firebase_service_account: false,
        })
      } else {
        throw e
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async pollOnce({ commit, state }, { hubId }) {
    if (!state.activeBuild) return
    const { data } = await axios.get(`/api/mobile-apps/hubs/${hubId}/builds/${state.activeBuild.build_id}`)
    commit('SET_ACTIVE_BUILD', data)
    return data
  },

  async startPollingBuild({ state, commit, dispatch }, { hubId }) {
    if (state.pollingTimer) return
    const tick = async () => {
      try {
        const build = await dispatch('pollOnce', { hubId })
        if (isBuildTerminal(build)) {
          clearInterval(state.pollingTimer)
          commit('SET_POLLING_TIMER', null)
        }
      } catch (e) {
        // transient errors shouldn't kill polling
      }
    }
    const timer = setInterval(tick, 10000)
    commit('SET_POLLING_TIMER', timer)
    await tick()
  },

  stopPollingBuild({ state, commit }) {
    if (state.pollingTimer) {
      clearInterval(state.pollingTimer)
      commit('SET_POLLING_TIMER', null)
    }
  },

  // M2: centralized panel-change gate. Per Milan post-impl: leaving the
  // settings panel must be blocked while app_name is blank. The default
  // app_name is hub.title (seeded in fetchConfig), so this is a rare edge
  // case but a real one (user explicitly clears the field).
  //
  // All panel-change paths in the UI (X button, backdrop click, nav-tab
  // click, build-history button toggle) dispatch this action instead of
  // committing SET_OPEN_PANEL directly. Returns true if the change was
  // applied, false if blocked. The blocked path sets fieldErrors.app_name
  // so SettingsPanel can show the inline validation. UPDATE_CONFIG_FIELD
  // auto-clears that error as soon as the user types a non-blank value.
  attemptChangePanel({ commit, state }, target) {
    const leavingSettings = state.openPanel === 'settings' && target !== 'settings'
    if (leavingSettings) {
      const name = (state.config && state.config.app_name) || ''
      if (!String(name).trim()) {
        commit('SET_FIELD_ERRORS', {
          ...state.fieldErrors,
          app_name: 'App name is required.',
        })
        return false
      }
    }
    commit('SET_OPEN_PANEL', target)
    return true
  },

  async triggerBuild({ commit, dispatch }, { hub, platforms }) {
    const { data } = await axios.post(
      `/api/mobile-apps/hubs/${hub.id}/builds`,
      { platforms }
    )
    commit('SET_ACTIVE_BUILD', {
      ...data,
      platform_builds: platforms.map(p => ({ platform: p, status: 'queued' })),
    })
    dispatch('startPollingBuild', { hubId: hub.id })
  },

  async retryOneSignalProvisioning({ commit }, { hub }) {
    const { data } = await axios.post(`/api/mobile-apps/hubs/${hub.id}/provision-onesignal`)
    const resource = data.data
    const {
      has_ios_store_credentials, has_android_store_credentials,
      has_apple_push_cert, has_firebase_service_account,
      ...config
    } = resource
    commit('SET_CONFIG', config)
    commit('SET_PRESENCE', {
      has_ios_store_credentials, has_android_store_credentials,
      has_apple_push_cert, has_firebase_service_account,
    })
    return resource
  },

  async togglePlatformEnabled({ commit, dispatch }, { platform, enabled, hub }) {
    const field = platform === 'ios' ? 'push_enabled_ios' : 'push_enabled_android'
    commit('UPDATE_CONFIG_FIELD', { field, value: enabled })
    await dispatch('save', { hub })
  },

  async togglePlatformBuild({ commit, dispatch }, { platform, field, value, hub }) {
    // platform: 'ios' | 'android' (not used today; carried for future logging)
    // field:    'ios_enabled' | 'android_enabled' | 'universal_links_enabled' | 'app_links_enabled'
    commit('UPDATE_CONFIG_FIELD', { field, value })
    await dispatch('save', { hub })
  },

  async retryProvisioning(_, { hub }) {
    await axios.post(`/api/mobile-apps/hubs/${hub.id}/provision-onesignal`)
  },

  async fetchBuildHistory({ commit }, { hub, page = 1 }) {
    commit('SET_BUILD_HISTORY_LOADING', true)
    try {
      const { data } = await axios.get(`/api/mobile-apps/hubs/${hub.id}/builds`, { params: { page } })
      commit('SET_BUILD_HISTORY', data.data || [])
      commit('SET_BUILD_HISTORY_META', {
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      })
      return data.data || []
    } catch (e) {
      // See fetchConfig note on the axios interceptor shape.
      if (e && e.status === 404) {
        commit('SET_BUILD_HISTORY', [])
        commit('SET_BUILD_HISTORY_META', { current_page: 1, last_page: 1, per_page: 20, total: 0 })
        return []
      }
      throw e
    } finally {
      commit('SET_BUILD_HISTORY_LOADING', false)
    }
  },
}

const getters = {
  isDirty: (state) =>
    !isEqual(state.config, state.originalConfig)
    || Object.keys(state.stagedCredentials).length > 0,
  hasIosCredentials: (state) => !!state.presence.has_ios_store_credentials,
  hasAndroidCredentials: (state) => !!state.presence.has_android_store_credentials,
  hasApplePushCert: (state) => !!state.presence.has_apple_push_cert,
  hasFirebaseServiceAccount: (state) => !!state.presence.has_firebase_service_account,

  appDisplayName(state) {
    return (state.config && state.config.app_name) || 'App'
  },

  displayVersion(state) {
    if (!state.config) return 'V: —'
    const ios = state.config.ios_version
    const android = state.config.android_version_name
    const v = ios || android
    return v ? `V: ${v}` : 'V: —'
  },

  platformStatusItems(state) {
    const activeByPlatform = {}
    if (state.activeBuild && Array.isArray(state.activeBuild.platform_builds)) {
      for (const pb of state.activeBuild.platform_builds) {
        activeByPlatform[pb.platform] = pb.status
      }
    }

    const historyByPlatform = {}
    for (const build of state.buildHistory || []) {
      for (const pb of build.platform_builds || []) {
        if (historyByPlatform[pb.platform] === undefined) {
          historyByPlatform[pb.platform] = pb.status
        }
      }
    }

    return ['ios', 'android'].map((platform) => ({
      platform: platform === 'ios' ? 'iOS' : 'Android',
      status: formatPlatformStatus(
        activeByPlatform[platform] !== undefined
          ? activeByPlatform[platform]
          : historyByPlatform[platform] || null
      ),
    }))
  },

  onesignalProvisioningStatus(state) {
    return (state.config && state.config.onesignal_provisioning_status) || 'idle'
  },

  hasPushAlert: () => false,

  pushReadinessWarnings(state) {
    const c = state.config || {}
    const p = state.presence || {}
    const out = []

    if (c.push_enabled_ios) {
      if (!p.has_apple_push_cert) out.push({ platform: 'ios', code: 'p8_missing', message: 'iOS P8 file missing' })
      if (!p.has_apns_key_id)     out.push({ platform: 'ios', code: 'key_id_missing', message: 'APNs Key ID missing' })
      if (!c.ios_team_id)         out.push({ platform: 'ios', code: 'team_id_missing', message: 'Apple Team ID missing (set in App Store credentials)' })
      if (!c.ios_bundle_id)       out.push({ platform: 'ios', code: 'bundle_id_missing', message: 'iOS Bundle ID missing (set in App Settings)' })
    }

    if (c.push_enabled_android) {
      if (!p.has_firebase_service_account) {
        out.push({ platform: 'android', code: 'firebase_json_missing', message: 'Android Firebase JSON missing' })
      }
    }

    if (c.onesignal_provisioning_status === 'failed') {
      const platform = c.onesignal_provisioning_error_platform
      const message = (platform === 'apple' || platform === 'android')
        ? c.onesignal_provisioning_error
        : 'Push notification configuration failed. Re-upload your credentials and try again.'
      out.push({
        platform: null,
        code: 'provisioning_failed',
        message,
      })
    }

    return out
  },

  // M4 (Milan post-impl): blocking warnings for the preflight modal
  // (figma 11422:305738). Returned as an ordered list so the UI renders
  // them top-to-bottom in this priority. Each entry has:
  //   key        — stable identifier (for v-for + tests)
  //   message    — body text shown in the warning notice
  //   ctaLabel   — link button text (chevron auto-appended in UI)
  //   ctaTarget  — { type: 'panel'|'modal'|'external', value }
  //
  // Intent matrix:
  //   - publishRequest set  → intent = publishRequest.platforms (retry mode)
  //   - publishRequest null → intent = both platforms (default mode warns
  //                            for any missing credential, including the
  //                            no-creds-at-all case which used to fire a
  //                            separate toast — now folded into the same
  //                            preflight surface).
  //
  // App icon + push warnings are global (apply regardless of platform).
  blockingWarnings(state, getters) {
    const c = state.config || {}
    const p = state.presence || {}
    const intent = state.publishRequest && Array.isArray(state.publishRequest.platforms)
      ? state.publishRequest.platforms.slice()
      : ['ios', 'android']  // default mode covers both platforms

    const out = []

    // --- iOS credentials ---
    if (intent.includes('ios') && !p.has_ios_store_credentials) {
      out.push({
        key: 'ios-creds-missing',
        severity: 'warning',
        message: 'Add your Apple credentials in Settings to publish to iOS.',
        ctaLabel: 'App Settings',
        ctaTarget: { type: 'panel', value: 'settings' },
      })
    }

    // --- Android credentials ---
    if (intent.includes('android') && !p.has_android_store_credentials) {
      out.push({
        key: 'android-creds-missing',
        severity: 'warning',
        message: 'Add your Android credentials in Settings to publish to Android.',
        ctaLabel: 'App Settings',
        ctaTarget: { type: 'panel', value: 'settings' },
      })
    }

    // --- App icon (global) ---
    // No fallback chain check: figma copy says "Upload your app icon"
    // explicitly, implying a custom icon is required even when the hub
    // already has an avatar that resolveIconImageUrl would surface.
    if (!c.icon_image_url) {
      out.push({
        key: 'app-icon-missing',
        severity: 'warning',
        message: 'Upload your app icon before publishing.',
        ctaLabel: 'App Icon',
        ctaTarget: { type: 'panel', value: 'icon' },
      })
    }

    // --- Push notifications (global) ---
    // Per-platform failure mode:
    //   'not-configured'       — gate unmet (creds missing)
    //   'provisioning-failed'  — creds present, enabled, OneSignal rejected our PUT
    //   null                   — no warning (opt-out or fine)
    //
    // The publish modal shows ONE push row regardless of platform count
    // (Figma 11422:305738). When both platforms contribute a mode, the
    // worse signal wins: provisioning-failed > not-configured.
    const pushFailureMode = (platform) => {
      const configured = platform === 'ios'
        ? !!(p.has_apple_push_cert && p.has_apns_key_id)
        : !!p.has_firebase_service_account
      if (!configured) return 'not-configured'
      const enabled = platform === 'ios' ? !!c.push_enabled_ios : !!c.push_enabled_android
      if (!enabled) return null                                       // opted out — no warning
      if (c.onesignal_provisioning_status === 'failed') return 'provisioning-failed'
      return null
    }

    const modes = intent.map(pushFailureMode).filter(Boolean)
    if (modes.includes('provisioning-failed')) {
      const platform = c.onesignal_provisioning_error_platform
      const message = (platform === 'apple' || platform === 'android')
        ? c.onesignal_provisioning_error
        : 'Push notification configuration failed. Re-upload your credentials and try again.'
      out.push({
        key: 'push-provisioning-failed',
        severity: 'error',
        message,
        ctaLabel: 'Notifications',
        ctaTarget: { type: 'panel', value: 'notifications' },
      })
    } else if (modes.includes('not-configured')) {
      out.push({
        key: 'push-not-configured',
        severity: 'warning',
        message: 'Set up push notifications before publishing.',
        ctaLabel: 'Notifications',
        ctaTarget: { type: 'panel', value: 'notifications' },
      })
    }

    return out
  },
}

export default { namespaced, state, mutations, actions, getters }
